import React, { useState, useEffect } from 'react';
import ImageUploading, {
  ImageListType,
  ErrorsType,
} from 'react-images-uploading';

import { useIntl } from 'react-intl';
import { ProgressSpinner } from 'primereact/progressspinner';

import {
  useAPIUploadImgToS3,
  MAX_SIZE_IMAGE,
  MAX_NUMBER_IMG,
} from '@marketplace/api';
import { Icon } from '@marketplace/ui-elements';

import classes from './uploader.module.scss';

type TImgUrl = {
  preview_images_url: string[];
  recovery_images_url: string[];
};

type TUploaderImgProps = {
  isContentEditable: boolean;
  images: any[];
  setImages: React.Dispatch<any>;
  setValue: any;
  getValues: any;
  fieldId: string;
  onRemoveImgUrl: React.Dispatch<any>;
  imagesUrl: TImgUrl;
  setError: React.Dispatch<any>;
  error: string;
  errors: any;
  clearErrors?: any;
  trigger?: any;
};

export const UploaderImg = ({
  fieldId,
  isContentEditable,
  setImages,
  images,
  setValue,
  getValues,
  imagesUrl,
  setError,
  trigger,
  error,
  errors,
  clearErrors,
  onRemoveImgUrl,
}: TUploaderImgProps) => {
  const { formatMessage } = useIntl();
  const { uploadImgToS3, data, loading } = useAPIUploadImgToS3();
  const maximumNumberOfPhotos =
    MAX_NUMBER_IMG - imagesUrl.preview_images_url.length;

  useEffect(() => {
    if (data) {
      setError('');
      setValue('preview_images', [...getValues('preview_images'), data], {
        shouldDirty: true,
      });
    }
  }, [data]);

  const onChange = (
    imageList: ImageListType,
    addUpdateIndex: number[] | undefined
  ) => {
    if (imageList.length > images.length) {
      imageList.forEach((element: any, index) => {
        if (index > images.length - 1) {
          uploadImgToS3(element.file);
          clearErrors('preview_images')
          trigger();
        }
      });
    }
    setImages(imageList as never[]);
  };

  const onError = (errors: ErrorsType) => {
    if (errors?.acceptType) {
      setError(formatMessage({ id: 'developer_personal_page_type_error' }));
    }
    if (errors?.maxFileSize) {
      setError(formatMessage({ id: 'description_max_size' }));
    }
    if (errors?.maxNumber) {
      setError(formatMessage({ id: 'description_max_number' }));
    }
  };

  const onNameImageRemove = (index: number) => {
    const arr = getValues('preview_images');
    const deleteArr = arr.splice(index, 1);
    const result = arr.filter((item: any) => item !== deleteArr[0]);

    setValue('preview_images', result, { shouldDirty: true });
  };


  return (
    <ImageUploading
      multiple
      value={images}
      onChange={onChange}
      maxNumber={maximumNumberOfPhotos}
      maxFileSize={MAX_SIZE_IMAGE}
      acceptType={['jpg', 'png']}
      onError={onError}
      inputProps={{ id: fieldId, name: fieldId }}
    >
      {({ imageList, onImageUpload, onImageRemove, isDragging, dragProps }) => (
        <>
       
        <div className={classes.uploader_label}>
            {formatMessage({ id: 'description_preview_img' })}{' '}
            <div className={classes.customError}>
              { errors?.preview_images?.message}
            </div>
          </div>
    
        
          <div
            className={
              loading
                ? classes.uploader_set_wrapper_loading
                : classes.uploader_set_wrapper
            }
          >
            {isContentEditable && (
              <div
                {...dragProps}
                style={isDragging ? { color: 'green' } : undefined}
                className={classes.uploader_set_wrapper_inner}
              >
                <div className={classes.uploader_info_block}>
                  <span>{formatMessage({ id: 'description_drop_img' })}</span>
                  <span>
                    {formatMessage({ id: 'description_drop_img_or' })}
                  </span>
                  <span onClick={onImageUpload} className={classes.selectImage}>
                    select images
                  </span>
                  <span className={classes.formats}>
                    {formatMessage({ id: 'description_high_resolution_img' })}
                  </span>
                </div>
                <div className={classes.preview_img}>
                  {/* images that are loaded from the device */}
                  {imageList.map((image, index) => (
                    <div key={image.dataURL || index} className={classes.preview_img_block}>
                      <img src={image.dataURL} alt="" />
                      <div
                        onClick={() => {
                          onImageRemove(index);
                          onNameImageRemove(index);
                        }}
                        className={classes.remove_img}
                      >
                        <Icon glyph={Icon.glyphs.font['pi-times-circle']} />
                      </div>
                    </div>
                  ))}
                  {/* images that are loaded to DB*/}
                  {imagesUrl.preview_images_url.map(
                    (url: any, index: number) => (
                      <div key={url} className={classes.preview_img_block}>
                        <img src={url} alt="" />
                        <div
                          onClick={() => {
                            onRemoveImgUrl(index);
                          }}
                          className={classes.remove_img}
                        >
                          <Icon glyph={Icon.glyphs.font['pi-times-circle']} />
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
            {/* view mode */}
            {!isContentEditable && (
              <div className={classes.uploader_set_wrapper_inner_view}>
                {imagesUrl.preview_images_url.map((item: any) => (
                  <div key={item} className={classes.preview_img_view_mode}>
                    <img src={item} alt="" />
                  </div>
                ))}
              </div>
            )}
            {loading && (
              <div className={classes.p_progress_spinner}>
                <ProgressSpinner />
              </div>
            )}
            <span className={classes.error}>{error}</span>
          </div>
        </>
      )}
    </ImageUploading>
  );
};
